import { useState, useEffect } from 'react';
import { eventSnapshotListener, addEvent, updateEvent as updateFirebaseEvent, deleteEvent as deleteFirebaseEvent } from '../../../../repository/EventDAO';
import { useGoogleCalendarContext } from '../../../../context/google_calendar/GoogleCalendarContext';

export const useFirebaseEvents = (firebaseUser, organizationEmployees, organizationCustomers) => {
  const [events, setEvents] = useState([]);
  const { deleteGoogleEvent } = useGoogleCalendarContext();

  useEffect(() => {
    if (!firebaseUser) return;
    
    const unsubscribe = eventSnapshotListener(
      firebaseUser.organization,
      firebaseUser.uid,
      (fetchedEvents) => {
        const eventsWithColors = fetchedEvents.map((event) => {
          const employee = organizationEmployees.find(emp => emp.id === event.assigned_employees?.[0]);
          const customer = organizationCustomers.find(cust => cust.id === event.client_id);
          const color = employee?.calendar_color || customer?.calendar_color || '#3788d8';
          
          return {
            ...event,
            start: event.start,
            end: event.end,
            backgroundColor: color,
            borderColor: color,
            source: 'firebase'
          };
        });
        
        setEvents(eventsWithColors);
      }
    );

    return () => unsubscribe();
  }, [firebaseUser, organizationCustomers, organizationEmployees]);

  const handleDelete = async (firebaseEventId, googleEventId = null) => {
    try {
      // If there's a Google Calendar ID, delete from Google first
      if (googleEventId) {
        const googleResult = await deleteGoogleEvent(googleEventId);
        if (!googleResult) {
          return { success: false, error: 'Failed to delete from Google Calendar' };
        }
      }

      // If it's a Firebase event, delete from Firebase
      if (firebaseEventId) {
        const result = await deleteFirebaseEvent(firebaseUser.organization, firebaseEventId);
        return { success: result.success, error: result.error };
      }

      // If it's only a Google event
      if (!firebaseEventId && googleEventId) {
        const result = await deleteGoogleEvent(googleEventId);
        return { success: result, error: result ? null : 'Failed to delete from Google Calendar' };
      }

      return { success: true };
    } catch (error) {
      console.error('Error deleting event:', error);
      return { success: false, error: error.message };
    }
  };

  const handleUpdate = async (organizationId, eventId, eventData) => {
    try {
      const result = await updateFirebaseEvent(organizationId, eventId, eventData);
      return { success: result.success, error: result.error };
    } catch (error) {
      console.error('Error updating event:', error);
      return { success: false, error: error.message };
    }
  };

  return { 
    events, 
    addEvent, 
    updateEvent: handleUpdate, 
    deleteEvent: handleDelete 
  };
};